import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks';

/** Задержка записи ширины в настройки: панель отдаёт ширину на каждый пиксель перетаскивания */
const PERSIST_DELAY_MS = 500;

/** Ширина панели управления колонками и её обработчик для DataGrid */
type Result = {
  /** Текущая ширина панели в px; `undefined` — панель подбирает ширину под контент сама */
  width: number | undefined;
  /** Обработчик перетаскивания ручки — вызывается панелью на каждый пиксель */
  onWidthChange: (next: number) => void;
};

/**
 * Ширина панели управления колонками: мгновенное состояние для перетаскивания ручки
 * и отложенная запись в настройки таблицы.
 *
 * Панель контролируемая — ширину нужно возвращать ей на каждый пиксель перетаскивания,
 * поэтому значение живёт в локальном состоянии, а в настройки (и оттуда на бэкенд)
 * уходит с задержкой.
 *
 * @param savedWidth - Сохранённая ширина из настроек таблицы; приходит асинхронно, после авторизации
 * @param onPersist - Запись ширины в настройки таблицы
 */
export const useColumnsPanelWidth = (savedWidth: number | undefined, onPersist: (width: number) => void): Result => {
  const [width, setWidth] = useState<number | undefined>(savedWidth);
  const debouncedWidth = useDebouncedValue(width, PERSIST_DELAY_MS);

  // Настройки пользователя загружаются после авторизации, то есть уже после первого рендера.
  // Подхватываем сохранённое значение, пока пользователь не задал ширину сам.
  useEffect(() => {
    if (width === undefined && savedWidth !== undefined) {
      setWidth(savedWidth);
    }
  }, [savedWidth, width]);

  useEffect(() => {
    if (debouncedWidth === undefined || debouncedWidth === savedWidth) return;
    onPersist(debouncedWidth);
  }, [debouncedWidth, savedWidth, onPersist]);

  return { width, onWidthChange: setWidth };
};
